import 'mapbox-gl/dist/mapbox-gl.css';
import polyline from '@mapbox/polyline';
import { useState } from 'react';
import { useMeasure } from 'react-use';
import { simplify } from 'simplify-geojson';

import appConfig from 'src/core/config';
import { logger } from 'src/utils/datadog-log-wrapper';

import { useTranslation } from '../../hooks/useTranslation';

/**
 * The component uses the Static Images API from Mapbox https://docs.mapbox.com/api/maps/static-images/
 * The goal is to split usage between both mapbox gl API and static images API as the two APIS are free below 50k uses
 * (mapbox gl has an higher price once we go over 50k)
 *
 * To check the current usage (and prices) on mapbox go to https://account.mapbox.com/statistics/ (account in 1password)
 */

type Props = {
  className?: string;
  path: [number, number][];
};

export const StaticImageMap = ({ className, path }: Props) => {
  const { t } = useTranslation('global');
  const [ref, { width: refWidth, height: refHeight }] =
    useMeasure<HTMLDivElement>();
  const [hasError, setHasError] = useState(false);

  const isRetina = window.devicePixelRatio > 1;

  const MAPBOX_MAX_DIMENSION = isRetina ? 640 : 1280;
  // Safety but we should probably keep the ratio rather than taking the min
  // Ok for now as the images are shown in a small container
  const width = Math.ceil(Math.min(refWidth, MAPBOX_MAX_DIMENSION));
  const height = Math.ceil(Math.min(refHeight, MAPBOX_MAX_DIMENSION));

  const encodedPath = polyline.encode(
    simplify(
      path.map(([lat, long]) => [long, lat]),
      Math.max(path.length / 200_000, 0.000_01),
    ),
  );
  if (hasError) {
    logger.warn('Static Image Map failed to load', {
      team: 'travel-and-expenses',
      scope: 'Mileage::Map',
      pathLength: path?.length ?? 'none',
      encodedPath: encodedPath.length,
      isRetina,
      width,
      height,
    });
    return null;
  }

  return (
    <div className={className} ref={ref}>
      {width === 0 || height === 0 ? (
        <div className="rounded-t-12 h-full max-h-full w-full max-w-full" />
      ) : (
        <img
          className="rounded-t-12 h-full max-h-full w-full max-w-full object-contain"
          src={`https://api.mapbox.com/styles/v1/spendesk/cjpmiia6b00ng2sqj1h0r74nm/static/path-3+5d21d2(${encodeURIComponent(
            encodedPath,
          )})/auto/${width}x${height}${isRetina ? '@2x' : ''}?access_token=${appConfig.mapboxgl.accessToken}&logo=false`}
          alt={t('forms.mileageAllowance.journeyMap')}
          onError={() => setHasError(true)}
        />
      )}
    </div>
  );
};
